/**
 * Client side of the off-thread solver.
 *
 * Falls back to solving inline if a module worker is unavailable - some locked
 * down school networks and older classroom browsers will not give us one, and
 * the project's promise is that everything runs on ordinary classroom
 * computers. Degraded there means "the frame hitches", not "the feature is
 * missing".
 */

import { EtchSim } from './EtchSim'
import type { ChamberState, Recipe } from './recipe'
import type { SweepError, SweepReply, SweepRequest } from './sweep.worker'

export interface SweepOutcome {
  metrics: ReturnType<EtchSim['metrics']>
  samples: ReturnType<EtchSim['samples']>
}

export class SweepRunner {
  private worker: Worker | null = null
  private nextId = 1
  private readonly pending = new Map<
    number,
    { resolve: (v: SweepOutcome) => void; reject: (e: Error) => void }
  >()

  /** True when work is genuinely off the main thread. */
  readonly offThread: boolean

  constructor() {
    try {
      this.worker = new Worker(new URL('./sweep.worker.ts', import.meta.url), { type: 'module' })
      this.worker.onmessage = (e: MessageEvent<SweepReply | SweepError>) => this.onMessage(e.data)
      this.worker.onerror = () => this.degrade()
      this.offThread = true
    } catch {
      this.worker = null
      this.offThread = false
    }
  }

  private degrade(): void {
    // A worker that failed to start is worse than none: reject anything queued
    // so callers fall through to the inline path rather than hanging.
    for (const [, p] of this.pending) p.reject(new Error('sweep worker unavailable'))
    this.pending.clear()
    this.worker?.terminate()
    this.worker = null
  }

  private onMessage(msg: SweepReply | SweepError): void {
    const entry = this.pending.get(msg.id)
    if (!entry) return
    this.pending.delete(msg.id)
    if (msg.type === 'error') entry.reject(new Error(msg.message))
    else entry.resolve({ metrics: msg.metrics, samples: msg.samples })
  }

  async run(
    recipe: Recipe,
    chamber: ChamberState,
    patch: Partial<Recipe>,
    seconds = 10,
    dt = 2,
    chamberPatch: Partial<ChamberState> = {},
  ): Promise<SweepOutcome> {
    if (!this.worker) return this.runInline(recipe, chamber, patch, seconds, dt, chamberPatch)

    const id = this.nextId++
    const req: SweepRequest = { type: 'run', id, recipe, chamber, patch, chamberPatch, seconds, dt }
    return new Promise<SweepOutcome>((resolve, reject) => {
      this.pending.set(id, { resolve, reject })
      this.worker!.postMessage(req)
    }).catch(() => this.runInline(recipe, chamber, patch, seconds, dt, chamberPatch))
  }

  /** Inline fallback. Yields first so the caller can paint a progress state. */
  private async runInline(
    recipe: Recipe,
    chamber: ChamberState,
    patch: Partial<Recipe>,
    seconds: number,
    dt: number,
    chamberPatch: Partial<ChamberState>,
  ): Promise<SweepOutcome> {
    await new Promise((r) => setTimeout(r, 0))
    return EtchSim.predict({ recipe, chamber }, patch, seconds, dt, chamberPatch)
  }

  dispose(): void {
    this.worker?.terminate()
    this.worker = null
  }
}
